// 「錄影畫質」快捷已於 2026-08-20 移除（iOS/Android 已於 08-19 同批移除，web 是最後一個）：
// 它改的是**持久** `RecordQuality`（0x2100008050000200，影響裝置錄影，不是眼前這條串流），
// 權威位置在「裝置設定 › 錄影」。放在看畫面的頁面上是把 cluster 屬性表攤到 UI 上。
// ⚠️ web 這顆比手機版更危險：它是**直接循環套用**（720p→1080p→2K→4K→720p，模 4 回捲）、
// 連二次確認都沒有，誤點一下就把裝置的錄影畫質改掉，而且從 4K 按一次就掉回 720p。
//
// in-live 控制列快捷（APP#41，spec 15 §11）。即時影像中的常用開關快捷入口——
// 警笛（0x8081 AlarmFunction）/ 燈（0x8072 BulbSwitch，退回 0x8071 StatusLight）/
// 物件追蹤（0x8060 ObjectTracking）/ 手動事件（0x8050 ManualRecordStart CMD）。
//
// 這些皆是「既有 cluster 的 live 面快捷入口」，非新 cluster；走既有 op:set/op:cmd 機制
// （settings/ops.ts）。完整段落在 APP#40 設定頁。
//
// authz fail-closed（spec 21 §2.1）：capability_effective 未命中該 cluster、或無控制權
// （owner/admin 或 shared-user 具 `control` permission 之外）→ 該快捷不渲染（非 disable）；
// server 側 fail-closed 為權威。
// 串流中（ready）才可操作；op:get 讀當前狀態在 ready 後進行（此時 signaling 必已 open）。

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../lib/api';
import type { DeviceSummary } from './deviceModel';
import { canControlDevice } from './deviceModel';
import { DEVICE_INCLUDE } from './useDeviceList';
import { PTZ_OBJECT_TRACKING } from './ptz';
import { frameValue, opCmd, opGet, opSet } from './settings/ops';
import {
  CL_FLOODLIGHT,
  CL_LIGHT,
  CL_PTZ,
  CL_RECORDING,
  CL_SIREN,
  FLOOD_SWITCH,
  LIGHT_STATUS,
  REC_MANUAL_START,
  SIREN_ALARM,
  SIREN_DURATION,
  SIREN_TRIGGER,
} from './settings/ids';

interface ControlCaps {
  clusters: Set<string>;
  canControl: boolean;
  isOwner: boolean;
}

/** 自取 capability + role/permissions（一次）。取不到 → fail-closed（無 cluster、不可控）。 */
function useControlCaps(deviceId: string): ControlCaps | undefined {
  const [caps, setCaps] = useState<ControlCaps | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    void apiFetch<DeviceSummary>(`/v3/devices/${encodeURIComponent(deviceId)}`, { query: { include: DEVICE_INCLUDE } })
      .then((res) => {
        if (!alive) return;
        const set = new Set<string>();
        for (const c of res.data?.capability_effective ?? []) {
          if (c.cluster) set.add(c.cluster.toLowerCase());
        }
        const role = res.data?.role;
        setCaps({
          clusters: set,
          // 21 §2.1：owner/admin 或 shared-user 具 `control`（缺 role/DTO fail-closed）。
          canControl: res.data ? canControlDevice(res.data) : false,
          // RecordQuality 走 ADR-046 特判（Signalling 入向 owner-only）→ 畫質快捷維持 owner 閘。
          isOwner: role === 'owner' || role === 'admin',
        });
      })
      .catch(() => {
        if (alive) setCaps({ clusters: new Set(), canControl: false, isOwner: false }); // fail-closed
      });
    return () => {
      alive = false;
    };
  }, [deviceId]);
  return caps;
}

/** bool ATTR 快捷（op:get 讀初值 → op:set 樂觀 + rollback）。enabled 才讀初值。 */
function useBoolAttr(deviceId: string, id: string, enabled: boolean) {
  const [on, setOn] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    opGet(deviceId, id).then(
      (f) => {
        if (!live) return;
        const v = frameValue(f);
        setOn(v === true || v === 1);
      },
      () => {
        /* 讀不到初值 → 維持未知（首次點擊即以 !false=true 送出） */
      },
    );
    return () => {
      live = false;
    };
  }, [enabled, deviceId, id]);
  const toggle = () => {
    const next = !(on ?? false);
    const prev = on;
    setOn(next);
    setBusy(true);
    opSet(deviceId, id, next).then(
      () => {
        if (alive.current) setBusy(false);
      },
      () => {
        if (alive.current) {
          setBusy(false);
          setOn(prev); // rollback
        }
      },
    );
  };
  return { on, busy, toggle };
}

/**
 * 警笛「點放式」快捷（APP#130）：開＝先確保 AlarmFunction=true 佈防，再送 TriggerSiren 立即鳴響，
 * 本地起 SirenDuration 倒數；關＝AlarmFunction=false → 韌體即時靜音鳴叫中警笛（不需 StopSiren id）。
 * 倒數期滿裝置自停 → 自動回「關」。
 */
function useSiren(deviceId: string, enabled: boolean) {
  const [ringing, setRinging] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [busy, setBusy] = useState(false);
  const durationRef = useRef(10); // 讀不到 SirenDuration 時用韌體預設 10s
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    opGet(deviceId, SIREN_DURATION).then(
      (f) => {
        if (!live) return;
        const v = frameValue(f);
        if (typeof v === 'number' && v > 0) durationRef.current = v;
      },
      () => {},
    );
    return () => {
      live = false;
    };
  }, [enabled, deviceId]);
  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };
  const stopCountdown = () => {
    clearTimer();
    if (alive.current) {
      setRinging(false);
      setRemaining(0);
    }
  };
  const startCountdown = () => {
    clearTimer();
    setRinging(true);
    setRemaining(durationRef.current);
    timerRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearTimer();
          setRinging(false);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
  };
  const trigger = () => {
    startCountdown();
    setBusy(true);
    opSet(deviceId, SIREN_ALARM, true)
      .then(() => opCmd(deviceId, SIREN_TRIGGER))
      .then(
        () => {
          if (alive.current) setBusy(false);
        },
        () => {
          if (alive.current) setBusy(false);
          stopCountdown(); // 送失敗 → 收回「鳴響中」
        },
      );
  };
  const stop = () => {
    stopCountdown();
    setBusy(true);
    opSet(deviceId, SIREN_ALARM, false).then(
      () => {
        if (alive.current) setBusy(false);
      },
      () => {
        if (alive.current) setBusy(false);
      },
    );
  };
  return { ringing, remaining, busy, trigger, stop };
}

/**
 * in-live 控制列。掛在 Live 頁；自取 capability + role/permissions，只渲染命中且可控
 * （owner/admin 或 shared-user 具 `control`，21 §2.1）的快捷；畫質快捷（ADR-046
 * owner-only 特判 id）另以 owner 閘。ready=false（未串流）時整列 disable。無任何可用快捷 → 不渲染。
 */
export function LiveControlBar({ deviceId, ready }: { deviceId: string; ready: boolean }) {
  const { t } = useTranslation();
  const caps = useControlCaps(deviceId);
  const canControl = caps?.canControl ?? false;

  const hasSiren = canControl && (caps?.clusters.has(CL_SIREN) ?? false);
  // 燈：優先補光燈 BulbSwitch（0x8072，實體照明），退回狀態燈 StatusLight（0x8071）。
  const lightCluster = caps?.clusters.has(CL_FLOODLIGHT) ? CL_FLOODLIGHT : caps?.clusters.has(CL_LIGHT) ? CL_LIGHT : null;
  const hasLight = canControl && lightCluster != null;
  const lightId = lightCluster === CL_FLOODLIGHT ? FLOOD_SWITCH : LIGHT_STATUS;
  const hasTracking = canControl && (caps?.clusters.has(CL_PTZ) ?? false);
  const hasManualRec = canControl && (caps?.clusters.has(CL_RECORDING) ?? false);

  const siren = useSiren(deviceId, hasSiren && ready);
  const light = useBoolAttr(deviceId, lightId, hasLight && ready);
  const tracking = useBoolAttr(deviceId, PTZ_OBJECT_TRACKING, hasTracking && ready);

  // 手動事件（CMD，瞬時）：送出 → 短暫「已觸發」回饋。
  const [meBusy, setMeBusy] = useState(false);
  const [meDone, setMeDone] = useState(false);
  const doneTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (doneTimer.current) clearTimeout(doneTimer.current);
  }, []);
  const fireManualEvent = () => {
    setMeBusy(true);
    opCmd(deviceId, REC_MANUAL_START).then(
      () => {
        setMeBusy(false);
        setMeDone(true);
        if (doneTimer.current) clearTimeout(doneTimer.current);
        doneTimer.current = setTimeout(() => setMeDone(false), 2000);
      },
      () => setMeBusy(false),
    );
  };

  if (!caps) return null; // capability 載入中
  if (!hasSiren && !hasLight && !hasTracking && !hasManualRec) return null; // 無可用快捷（無 control 權限 / 無相關 cluster）

  return (
    <div className="lv-ctrlbar" role="group" aria-label={t('liveCtrl.groupLabel')} data-testid="live-ctrlbar">
      {hasSiren && (
        <button
          type="button"
          className={siren.ringing ? 'btn-primary' : 'btn-ghost'}
          disabled={!ready || siren.busy}
          // 開：即時開（確認後送 TriggerSiren 立即鳴響）；鳴響中再點即「關」（靜音，免確認）。
          onClick={() => {
            if (siren.ringing) siren.stop();
            else if (window.confirm(t('liveCtrl.sirenConfirm'))) siren.trigger();
          }}
          aria-pressed={siren.ringing}
          data-testid="live-ctrl-siren"
        >
          <span aria-hidden="true">🚨</span>{' '}
          {siren.ringing ? t('liveCtrl.sirenRinging', { s: siren.remaining }) : t('liveCtrl.siren')}
        </button>
      )}
      {hasLight && (
        <button
          type="button"
          className={light.on ? 'btn-primary' : 'btn-ghost'}
          disabled={!ready || light.busy}
          onClick={light.toggle}
          aria-pressed={light.on ?? false}
          data-testid="live-ctrl-light"
        >
          <span aria-hidden="true">💡</span> {t('liveCtrl.light')}
        </button>
      )}
      {hasTracking && (
        <button
          type="button"
          className={tracking.on ? 'btn-primary' : 'btn-ghost'}
          disabled={!ready || tracking.busy}
          onClick={tracking.toggle}
          aria-pressed={tracking.on ?? false}
          data-testid="live-ctrl-tracking"
        >
          <span aria-hidden="true">🎯</span> {t('liveCtrl.tracking')}
        </button>
      )}
      {hasManualRec && (
        <button
          type="button"
          className="btn-ghost"
          disabled={!ready || meBusy}
          onClick={fireManualEvent}
          data-testid="live-ctrl-manual-event"
        >
          <span aria-hidden="true">⏺</span> {meDone ? t('liveCtrl.manualEventDone') : t('liveCtrl.manualEvent')}
        </button>
      )}
    </div>
  );
}
